import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { useMe } from "./api/hooks";
import { RequireAuth } from "./auth/RequireAuth";
import { Layout } from "./components/Layout";
import { AdminLayout } from "./components/AdminLayout";
import { LoginPage } from "./pages/LoginPage";
import { LocationsPage } from "./pages/LocationsPage";
import { AdminLocationsPage } from "./pages/AdminLocationsPage";
import { PreviewPage } from "./pages/PreviewPage";
import { RulesPage } from "./pages/RulesPage";
import { ActivityPage } from "./pages/ActivityPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { UsagePage } from "./pages/UsagePage";
import { ResultsPage } from "./pages/ResultsPage";
import { DirectoryPage } from "./pages/DirectoryPage";
import { SystemPage } from "./pages/SystemPage";
import { AdminOverviewPage } from "./pages/AdminOverviewPage";
import { AdminsPage } from "./pages/AdminsPage";
import { ApiClientsPage } from "./pages/ApiClientsPage";
import { StaffPage } from "./pages/StaffPage";

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });

/** "/" goes to the dashboard for the signed-in role. */
function Home() {
  const me = useMe().data!;
  return <Navigate to={me.role === "admin" ? "/admin" : "/locations"} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
      <Route element={<RequireAuth ownerOnly><Layout /></RequireAuth>}>
        <Route path="/locations" element={<LocationsPage />} />
        <Route path="/locations/:blvdId/preview" element={<PreviewPage />} />
        <Route path="/preview" element={<PreviewPage />} />
        <Route path="/rules" element={<RulesPage variant="owner" />} />
        <Route path="/staff" element={<StaffPage variant="owner" />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="/results" element={<ResultsPage variant="owner" />} />
      </Route>
      <Route path="/admin" element={<RequireAuth admin><AdminLayout /></RequireAuth>}>
        <Route index element={<AdminOverviewPage />} />
        <Route path="locations" element={<AdminLocationsPage />} />
        <Route path="locations/:blvdId/preview" element={<PreviewPage />} />
        <Route path="preview" element={<PreviewPage />} />
        <Route path="rules" element={<RulesPage variant="admin" />} />
        <Route path="staff" element={<StaffPage variant="admin" />} />
        <Route path="activity" element={<ActivityPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="results" element={<ResultsPage variant="admin" />} />
        <Route path="directory" element={<DirectoryPage />} />
        <Route path="system" element={<SystemPage />} />
        <Route path="admins" element={<RequireAuth superAdmin><AdminsPage /></RequireAuth>} />
        <Route path="api-clients" element={<RequireAuth superAdmin><ApiClientsPage /></RequireAuth>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter><AppRoutes /></BrowserRouter>
    </QueryClientProvider>
  );
}
